import Typography from "@mui/material/Typography";
import { faHeart } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { faHeart as faHeartHollow } from "@fortawesome/pro-regular-svg-icons/faHeart";
import { sentenceCase } from "@portal/app/_common/lib/changeCase";

import { DreamSearchViewerResult } from "@portal/app/_common/types/dreamSearch";
import {
  DreamSearchResultMarkdownDescription,
  DreamSearchResultProgramHighlights,
  DreamSearchResultProgramName,
} from "./DreamSearchResultPrimitives";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { styled, useTheme } from "@mui/material/styles";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";
import Chip from "@mui/material/Chip";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import { useToggleFavorite } from "../../utils/useToggleFavorite";
import { ContentWithMiddots } from "@portal/app/_components/ui-elements/ContentWithMiddots";
import { DreamSearchResultLabel } from "./DreamSearchResultLabel";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import { DreamSearchResult } from "./DreamSearchResult";

export const DreamSearchProgramItem = ({
  i,
  item,
  eventName,
  programHeadingComponent = "h3",
}: {
  i: number;
  item: DreamSearchViewerResult;
  eventName: MixpanelEventName;
  programHeadingComponent?: "h3" | "span";
}) => {
  const theme = useTheme();
  const toggleFavorite = useToggleFavorite();

  return (
    <Box sx={{ position: "relative", display: "grid", minWidth: 0 }}>
      <DreamSearchResult
        i={i}
        title={item.provider.fullName}
        titleChips={
          !item.provider.isPartnered ? (
            <Chip
              sx={{
                backgroundColor: theme.palette.dream.grayMediumDark,
                color: "white",
              }}
              label="NOT AVAILABLE ON DREAMBOUND"
              size="small"
              data-testid={`not-available-on-dreambound-${item.id}`}
            />
          ) : undefined
        }
        eventName={eventName}
        logoUrl={item.provider.logoImageFile?.url}
        logoBackgroundColor={item.provider.detailsMap.logoPalette?.muted}
        content={
          <>
            <ContentHeaderContainer sx={{ pr: item.provider.isPartnered ? 4 : 0 }}>
              <DreamSearchResultProgramName
                component={programHeadingComponent}
                data-testid={kebabCase(`program-vocation-${item.program.vocation.adjective}`)}
              >
                {item.program.name}
              </DreamSearchResultProgramName>
            </ContentHeaderContainer>
            <ContentDetailsContainer>
              <ContentWithMiddots>
                {!item.program.environmentCompliance && (
                  <Typography variant="disclaimer" component="span">
                    {sentenceCase(item.program.environment)}
                  </Typography>
                )}
                {item.program.environment !== "ONLINE" && (
                  <Typography variant="disclaimer" component="span">
                    {`${item.distance.toFixed(0)} mi away`}
                  </Typography>
                )}
              </ContentWithMiddots>
            </ContentDetailsContainer>
            <ContentInfoContainer>
              {item.program.programHighlights.length > 0 ? (
                <Typography variant="disclaimer" component="span">
                  <DreamSearchResultProgramHighlights>
                    {item.program.programHighlights.map((highlight) => (
                      <li key={highlight}>
                        <Typography variant="disclaimer" component="div">
                          {highlight}
                        </Typography>
                      </li>
                    ))}
                  </DreamSearchResultProgramHighlights>
                </Typography>
              ) : item.program.publicDisplayFields?.aboutProgramPlainText ? (
                <DreamSearchResultMarkdownDescription
                  value={item.program.publicDisplayFields.aboutProgramPlainText}
                />
              ) : null}
            </ContentInfoContainer>
            {item.label && <DreamSearchResultLabel label={item.label} />}
          </>
        }
        item={item}
      />
      {item.provider.isPartnered && (
        <FavoriteContainer
          aria-label={`Favorite ${item.program.name} at ${item.provider.fullName}`}
          aria-pressed={item.favorited}
          onClick={() => toggleFavorite(item)}
          sx={{
            color: item.favorited ? paletteOptions.error.main : paletteOptions.text.primary,
          }}
        >
          <FontAwesomeIcon size="xl" icon={item.favorited ? faHeart : faHeartHollow} />
        </FavoriteContainer>
      )}
    </Box>
  );
};

const ContentHeaderContainer = styled(Box)`
  display: flex;
  align-items: start;
  justify-content: space-between;
`;

const FavoriteContainer = styled(IconButton)`
  position: absolute;
  top: 5.5rem;
  right: 0;
  padding: 1.5rem;
  font-size: 0.7rem;

  &.Mui-focusVisible {
    outline: 2px solid currentColor;
    outline-offset: -4px;
  }
`;

const ContentDetailsContainer = styled(Box)`
  .MuiTypography-root {
    font-size: 14px;
  }

  display: flex;
  gap: 0.25rem;
`;

const ContentInfoContainer = styled(Box)`
  .editor-paragraph {
    margin-bottom: 0;
  }
`;
